import React from "react";
import { Box, MainText, PressableOpacity } from "app/components/common";
import FastImage from "react-native-fast-image";
import { IReviewMessage } from "@microdoing/shared/services/notification/types";
import theme from "app/Theme/Theme";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserId,
  getUserTimezone,
} from "@microdoing/shared/services/user/selectors";
import redirectNavigation from "app/utils/redirectionNotification";
import { useNavigation } from "@react-navigation/native";
import i18n from "@microdoing/shared/i18n";
import moment from "moment";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import MessageNotification from "app/components/Icons/Notifications/MessageNotification";
import PulseNotification from "app/components/Icons/Notifications/PulseNotification";
import PollNotification from "app/components/Icons/Notifications/PollNotification";
import SurveyNotification from "app/components/Icons/Notifications/SurveyNotification";
import CoachNotification from "app/components/Icons/Notifications/CoachNotification";
import CoachingNotification from "app/components/Icons/Notifications/CoachingNotification";
import ResourceNotification from "app/components/Icons/Notifications/ResourceNotification";
import ReviewNotification from "app/components/Icons/Notifications/ReviewNotification";
import StepCommentNotification from "app/components/Icons/Notifications/StepCommentNotification";
import LikeNotification from "app/components/Icons/Notifications/LikeNotification";
import OtherNotification from "app/components/Icons/Notifications/OtherNotification";
import normalize from "app/utils/normalize";

interface IProps {
  notification: IReviewMessage;
}

const NotificationRow = ({ notification }: IProps) => {
  const userId = useSelector(getUserId);
  const timezone = useSelector(getUserTimezone);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const {
    picture,
    text,
    type,
    event,
    data,
    createdAt,
    readBy,
    _id,
  } = notification;
  const seen = readBy.findIndex(el => el.readerId === userId) > -1;

  const displayDate = date => {
    const current = moment().startOf("day");
    const doneDate = moment.tz(date, timezone);
    const hour = doneDate.format("HH:mm");
    const lastDate = doneDate.startOf("day");
    const diff = current.diff(lastDate, "days");
    if (diff === 0) {
      return i18n.t("today-at", { hour });
    } else if (diff === 1) {
      return i18n.t("yesterday-at", { hour });
    }
    return i18n.t("ago-at-no-hour", { days: diff });
  };

  const navigateToEvent = () => {
    const { navigationRoute, navigationParams } = redirectNavigation({
      event,
      type,
      _id,
      userId,
      dispatch,
    });
    if (navigationRoute) {
      navigation.navigate(navigationRoute, navigationParams);
    }
  };

  const renderIcon = () => {
    if (type === "pulse") {
      return <PulseNotification />;
    } else if (type === "idea-comment") {
      return <OtherNotification />;
    } else if (type === "randomCoffee") {
      return <OtherNotification />;
    } else if (type === "poll") {
      return <PollNotification />;
    } else if (type === "survey") {
      return <SurveyNotification />;
    } else if (type === "coaching") {
      return <CoachingNotification />;
    } else if (type === "resource") {
      return <ResourceNotification />;
    } else if (type === "review") {
      return <ReviewNotification />;
    } else if (type === "message") {
      return <MessageNotification />;
    } else if (type === "stepComment") {
      return <StepCommentNotification />;
    } else if (type === "stepCommentLike") {
      return <LikeNotification />;
    } else if (type === "administratorNotification") {
      return <CoachNotification />;
    }
  };

  return (
    <PressableOpacity
      style={[styles.notificationView, seen && styles.notificationSeen]}
      onPress={navigateToEvent}
    >
      <Box flexDirection="row">
        <FastImage style={styles.avatar} source={{ uri: picture }} />
        <Box
          position="absolute"
          alignSelf="flex-end"
          right={-normalize(6)}
          bottom={-normalize(6)}
        >
          {renderIcon()}
        </Box>
      </Box>
      <Box
        marginLeft={isWebDesktop ? "m" : "sl"}
        flex={1}
        justifyContent="center"
      >
        <MainText variant="l4-medium">{text}</MainText>
        <MainText marginTop={2} variant="l3-medium" color="neutral.500">
          {displayDate(data?.sendingDate || createdAt)}
        </MainText>
      </Box>
    </PressableOpacity>
  );
};

const styles = {
  notificationView: {
    paddingVertical: isWebDesktop ? 10 : theme.spacing.sm,
    paddingRight: 30,
    paddingLeft: isWebDesktop ? 30 : theme.spacing.sm,
    backgroundColor: theme.colors["brand.100"],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  notificationSeen: {
    backgroundColor: theme.colors["neutral.white"],
  },
  avatar: {
    width: normalize(40),
    height: normalize(40),
    borderRadius: 30,
    borderWidth: 1,
    borderColor: "white",
  },
};

export default NotificationRow;
